import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, User, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";
import { api, V1 } from "@/lib/api";

function renderMarkdown(md) {
  if (!md) return null;
  // very small markdown renderer: h1/h2/h3, paragraphs, lists
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];
  const flush = () => { if (listBuf.length) { out.push({ type: "ul", items: listBuf }); listBuf = []; } };
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (line.startsWith("# ")) { flush(); out.push({ type: "h1", text: line.slice(2) }); }
    else if (line.startsWith("## ")) { flush(); out.push({ type: "h2", text: line.slice(3) }); }
    else if (line.startsWith("### ")) { flush(); out.push({ type: "h3", text: line.slice(4) }); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { listBuf.push(line.slice(2)); }
    else if (line === "") { flush(); }
    else { flush(); out.push({ type: "p", text: line }); }
  });
  flush();
  return out.map((b, i) => {
    if (b.type === "h1") return <h1 key={i} className="font-display text-3xl md:text-4xl font-bold mt-8 mb-4 tracking-tight">{b.text}</h1>;
    if (b.type === "h2") return <h2 key={i} className="font-display text-2xl md:text-3xl font-bold mt-8 mb-3 tracking-tight">{b.text}</h2>;
    if (b.type === "h3") return <h3 key={i} className="font-display text-xl font-bold mt-6 mb-2">{b.text}</h3>;
    if (b.type === "ul") return <ul key={i} className="list-disc pl-6 space-y-2 my-3 text-foreground/90">{b.items.map((t, j) => <li key={j}>{t}</li>)}</ul>;
    return <p key={i} className="my-3 leading-relaxed text-foreground/90">{b.text}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setNotFound(false);
    api.get(`${V1}/blog/by-slug/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="p-16 text-center">Yazı bulunamadı.</div>;
  if (!post) return <div className="p-16 text-center text-muted-foreground">Yükleniyor…</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.seo_description || post.excerpt,
    image: post.cover_url,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: post.author || "SanoStudent" },
    publisher: { "@type": "Organization", name: "SanoStudent", logo: { "@type": "ImageObject", url: "https://bursluluksinavi.com/logo.png" } },
    mainEntityOfPage: `https://bursluluksinavi.com/blog/${post.slug}`,
  };

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-post-page">
      <SEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        canonical={`/blog/${post.slug}`}
        image={post.cover_url}
        type="article"
        keywords={(post.tags || []).join(", ")}
        jsonLd={jsonLd}
      />
      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-4">
        <Link to="/blog" className="hover:text-foreground">Blog</Link>
        <ChevronRight className="h-3 w-3" />
        <span>{post.category}</span>
      </div>
      <div className="overline text-accent mb-3">{post.category}</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-tight">{post.title}</h1>
      <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1"><User className="h-4 w-4" /> {post.author}</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> {new Date(post.created_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {post.read_minutes} dk okuma</span>
      </div>
      {post.cover_url && (
        <img src={post.cover_url} alt={post.title} className="w-full aspect-[16/8] object-cover rounded-xl border border-border mt-8" />
      )}
      <div className="mt-8">{renderMarkdown(post.content)}</div>

      {post.tags?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-2">
          {post.tags.map((t) => (
            <span key={t} className="px-3 py-1 text-xs bg-muted rounded">#{t}</span>
          ))}
        </div>
      )}

      {post.related?.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="font-display font-bold text-xl mb-4">Benzer Yazılar</div>
          <div className="grid md:grid-cols-3 gap-4">
            {post.related.map((r) => (
              <Link key={r.id} to={`/blog/${r.slug}`} className="p-4 rounded-lg border border-border bg-card hover:border-primary/40" style={{ transition: "border-color 0.2s" }}>
                <div className="overline text-accent mb-2">{r.category}</div>
                <div className="font-display font-bold line-clamp-2">{r.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
